<template>
  <div class="score-board-container">
    <div class="header">
      <h2>Score Board - Gestion des scores du test</h2>
      <div class="test-summary">
        <div class="summary-item">
          <span class="label">Total items :</span>
          <span class="value">{{ totalItems }}</span>
        </div>
        <div class="summary-item">
          <span class="label">Score maximum total :</span>
          <span class="value">{{ totalMaxScore }}</span>
        </div>
        <div class="summary-item">
          <span class="label">Items avec pénalités :</span>
          <span class="value">{{ itemsWithPenalties }}</span>
        </div>
      </div>
    </div>

    <!-- Histogramme des scores -->
    <div class="chart-section">
      <h3>Distribution des scores maximums par item</h3>
      <div class="chart-container">
        <canvas ref="chartCanvas" id="scoreChart"></canvas>
      </div>
      <div class="chart-legend">
        <div class="legend-item">
          <span class="legend-color standard"></span>
          <span>Score standard (1 point)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color high"></span>
          <span>Score élevé (> 1 point)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color low"></span>
          <span>Score faible (< 1 point)</span>
        </div>
      </div>
    </div>

    <!-- Graphique des pénalités -->
    <div class="chart-section">
      <h3>Distribution des pénalités par item</h3>
      <div v-if="hasAnyPenalties" class="chart-container">
        <canvas ref="penaltiesChartCanvas" id="penaltiesChart"></canvas>
      </div>
      <div v-else class="no-penalties-message">
        <div class="no-penalties-icon">✓</div>
        <p>No penalties</p>
        <small>Aucune pénalité n'est définie dans ce test</small>
      </div>
      <div v-if="hasAnyPenalties" class="chart-legend">
        <div class="legend-item">
          <span class="legend-color no-penalty"></span>
          <span>Aucune pénalité</span>
        </div>
        <div class="legend-item">
          <span class="legend-color light-penalty"></span>
          <span>Pénalité légère (< -0.5)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color heavy-penalty"></span>
          <span>Pénalité lourde (≥ -0.5)</span>
        </div>
      </div>
    </div>

    <div class="items-list">
      <div 
        v-for="(item, index) in elements" 
        :key="item._id"
        class="item-card"
        :class="{ 'has-penalties': hasPenalties(item) }"
      >
        <!-- En-tête de l'item -->
        <div class="item-header">
          <div class="item-number">Item {{ index + 1 }}</div>
          <div class="item-type">{{ getInteractionTypeLabel(item.el_Type) }}</div>
          <div class="item-multiple" v-if="item.multiple">
            <span class="badge">Multiple</span>
          </div>
        </div>

        <!-- Question -->
        <div class="item-question">
          <h4>{{ item.el_Text }}</h4>
          <div v-if="item.el_RichText" class="rich-text" v-html="item.el_RichText"></div>
        </div>

        <!-- Scoring Section -->
        <div class="scoring-section">
          <div class="score-info">
            <div class="max-score">
              <label>Score Maximum:</label>
              <input 
                type="number" 
                v-model.number="item.maxScore" 
                @change="updateMaxScore(item)"
                class="score-input"
                min="0"
                step="0.1"
              >
            </div>
          </div>

          <!-- Options et leurs poids -->
          <div class="options-scoring">
            <h5>Distribution des points par option :</h5>
            <div class="options-grid">
              <div
                v-for="option in item.options"
                :key="option.id || option._id"
                class="option-row"
                :class="{ 
                  'correct': option.isCorrect, 
                  'incorrect': !option.isCorrect && option.weight < 0,
                  'neutral': !option.isCorrect && option.weight === 0
                }"
              >
                <div class="option-text" v-html="option.text || option.richText || 'Option ' + (option.id || '')"></div>
                <div class="option-status">
                  <span v-if="option.isCorrect" class="status-badge correct">✓ Correct</span>
                  <span v-else class="status-badge incorrect">✗ Incorrect</span>
                </div>
                <div class="option-weight">
                  <label>Poids:</label>
                  <input 
                    type="number" 
                    v-model.number="option.weight" 
                    @change="updateOptionWeight(item, option)"
                    class="weight-input"
                    step="0.1"
                  >
                </div>
                <div class="penalty-indicator" v-if="option.weight < 0">
                  <span class="penalty-badge">Pénalité</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Résumé des scores -->
          <div class="score-summary">
            <div class="summary-row">
              <span>Points positifs maximum :</span>
              <span class="positive-score">{{ getMaxPositiveScore(item) }}</span>
            </div>
            <div class="summary-row" v-if="getMaxNegativeScore(item) < 0">
              <span>Pénalité maximum :</span>
              <span class="negative-score">{{ getMaxNegativeScore(item) }}</span>
            </div>
            <div class="summary-row">
              <span>Cohérence :</span>
              <span class="coherence-indicator" :class="getCoherenceStatus(item)">
                {{ getCoherenceMessage(item) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Actions rapides -->
        <div class="quick-actions">
          <button @click="normalizeScores(item)" class="btn btn-secondary">
            Normaliser sur {{ item.maxScore }}
          </button>
          <button @click="resetPenalties(item)" class="btn btn-secondary">
            Supprimer pénalités
          </button>
          <button @click="equalWeights(item)" class="btn btn-secondary">
            Poids égaux
          </button>
        </div>
      </div>
    </div>

    <!-- Actions globales -->
    <div class="global-actions">
      <button @click="normalizeAllScores" class="btn btn-primary">
        Normaliser tous les scores
      </button>
      <button @click="removeAllPenalties" class="btn btn-secondary">
        Supprimer toutes les pénalités
      </button>
      <button @click="validateScoring" class="btn btn-success">
        Valider la cohérence des scores
      </button>
    </div>
  </div>
</template>

<script setup>
import { useTestStore } from '../../stores/testStore'
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import { Chart, registerables } from 'chart.js'

// Enregistrer tous les composants Chart.js
Chart.register(...registerables)

const store = useTestStore()

// Normaliser les éléments pour s'assurer qu'ils ont tous une propriété options
const normalizeElement = (element) => {
  // Cas spécial pour HotSpot : convertir les zones en options
  if (element.el_Type === 'hotSpot' && element.el_Data && Array.isArray(element.el_Data)) {
    const options = element.el_Data.map(zone => ({
      id: zone.id,
      text: zone.name || `Zone ${zone.id}`,
      isCorrect: true,
      weight: zone.score || 1
    }))

    // Ajouter l'option pour les pénalités si elles existent
    const penalties = element.el_data?.penalties || element.el_Penalties || 0
    if (penalties !== 0) {
      options.push({
        id: `${element.el_ID}_wrong_click`,
        text: "Wrong click penalty",
        isCorrect: false,
        weight: -Math.abs(penalties)  // Négatif pour pénalité
      })
    }

    // Calculer maxScore comme somme des scores des zones
    const maxScore = element.el_Data.reduce((sum, zone) => sum + (zone.score || 0), 0)

    return {
      ...element,
      options,
      maxScore: maxScore || 1
    }
  }

  // Cas spécial pour Making Pairs : convertir les paires en options
  if (element.el_Type === 'makingPairs' && element.el_InteractionData?.expectedPairs) {
    const pairs = element.el_InteractionData.expectedPairs
    const pointsPerPair = element.el_InteractionData.pointsPerPair || 1

    const options = pairs.map((pair, index) => ({
      id: `${element.el_ID}_pair_${index}`,
      text: `${pair.left} ⇄ ${pair.right}`,
      isCorrect: true,
      weight: pointsPerPair
    }))

    // Calculer maxScore
    const maxScore = pairs.length * pointsPerPair

    return {
      ...element,
      options,
      maxScore: maxScore || 1
    }
  }

  // Cas spécial pour Order : convertir les labels en options
  if (element.el_Type === 'order' && element.labels && Array.isArray(element.labels)) {
    const weight = element.weight || 1  // Points pour l'ordre correct

    const options = [
      {
        id: `${element.el_ID}_correct_order`,
        text: `Correct order: ${element.labels.join(' → ')}`,
        isCorrect: true,
        weight: weight
      },
      {
        id: `${element.el_ID}_incorrect_order`,
        text: "Incorrect order",
        isCorrect: false,
        weight: 0
      }
    ]

    return {
      ...element,
      options,
      maxScore: weight
    }
  }

  // Cas spécial pour TextPuzzle : convertir les gaps et distractors en options
  if (element.el_Type === 'textPuzzle' && element.el_InteractionData) {
    const gaps = element.el_InteractionData.gaps || []
    const distractors = element.el_InteractionData.distractors || []
    const pointsPerCorrect = element.el_InteractionData.pointsPerCorrect || 1
    const penaltyPerIncorrect = element.el_InteractionData.penaltyPerIncorrect || 0

    const options = []

    // Ajouter les gaps (réponses correctes)
    gaps.forEach((gap, index) => {
      options.push({
        id: `${element.el_ID}_gap_${index}`,
        text: gap.text || `Gap ${index + 1}`,
        isCorrect: true,
        weight: pointsPerCorrect
      })
    })

    // Ajouter les distracteurs (réponses incorrectes)
    distractors.forEach((distractor, index) => {
      options.push({
        id: `${element.el_ID}_distractor_${index}`,
        text: `${distractor.text || `Distractor ${index + 1}`} (Distractor)`,
        isCorrect: false,
        weight: -Math.abs(penaltyPerIncorrect)  // Négatif pour pénalité
      })
    })

    // Calculer maxScore
    const maxScore = gaps.length * pointsPerCorrect

    return {
      ...element,
      options,
      maxScore: maxScore || 1
    }
  }

  // Cas spécial pour TextGaps : convertir les gaps en options
  if (element.el_Type === 'textGaps' && element.gaps && Array.isArray(element.gaps)) {
    const options = element.gaps.map((gap, index) => ({
      id: `${element.el_ID}_gap_${index}`,
      text: gap.label || `Gap ${index + 1}`,
      isCorrect: true,
      weight: gap.weight || 1
    }))

    // Calculer maxScore comme somme des poids
    const maxScore = element.gaps.reduce((sum, gap) => sum + (gap.weight || 1), 0)

    return {
      ...element,
      options,
      maxScore: maxScore || 1
    }
  }

  // Cas spécial pour Match : convertir la matrice en options
  if (element.el_Type === 'match' && element.el_Data) {
    const rows = element.el_Data.rows || []
    const columns = element.el_Data.columns || []
    const solutionMatrix = element.el_Data.solutionMatrix || []
    const scoring = element.el_Data.scoring || { right: 1, wrong: 0 }

    const options = []

    // Compter le nombre total de correspondances correctes
    let correctMatchesCount = 0
    solutionMatrix.forEach((row, rIndex) => {
      row.forEach((isCorrect, cIndex) => {
        if (isCorrect) {
          correctMatchesCount++
          options.push({
            id: `${element.el_ID}_match_${rIndex}_${cIndex}`,
            text: `${rows[rIndex]} ⇄ ${columns[cIndex]}`,
            isCorrect: true,
            weight: scoring.right || 1
          })
        }
      })
    })

    // Ajouter une option pour les mauvaises correspondances si pénalité existe
    if (scoring.wrong && scoring.wrong !== 0) {
      options.push({
        id: `${element.el_ID}_wrong_match`,
        text: "Wrong match penalty",
        isCorrect: false,
        weight: -Math.abs(scoring.wrong)
      })
    }

    // Calculer maxScore
    const maxScore = correctMatchesCount * (scoring.right || 1)

    return {
      ...element,
      options,
      maxScore: maxScore || 1
    }
  }

  // Cas spécial pour ShortAnswer : convertir les axes de correction en options
  if (element.el_Type === 'shortAnswer') {
    const correctionAxes = element.correctionAxes || []
    const useGrid = element.useGrid || false

    // Si useGrid est activé et qu'il y a des axes
    if (useGrid && correctionAxes.length > 0) {
      const options = correctionAxes.map((axis, index) => {
        const hasPositivePoints = axis.point && axis.point > 0
        const hasPenalty = axis.penalty && axis.penalty !== 0

        // Créer les options pour cet axe
        const axisOptions = []

        // Option pour les points positifs
        if (hasPositivePoints) {
          axisOptions.push({
            id: `${element.el_ID}_axis_${index}_positive`,
            text: `${axis.axis || `Axis ${index + 1}`} (if true)`,
            isCorrect: true,
            weight: axis.point
          })
        }

        // Option pour les pénalités
        if (hasPenalty) {
          axisOptions.push({
            id: `${element.el_ID}_axis_${index}_negative`,
            text: `${axis.axis || `Axis ${index + 1}`} (if false)`,
            isCorrect: false,
            weight: -Math.abs(axis.penalty)
          })
        }

        return axisOptions
      }).flat()

      // Calculer maxScore comme somme des points positifs
      const maxScore = correctionAxes.reduce((sum, axis) => sum + (axis.point || 0), 0)

      return {
        ...element,
        options,
        maxScore: maxScore || 1
      }
    }

    // Sinon, mode simple sans grille - retourner l'élément tel quel (sera géré par le fallback)
  }

  // Si l'élément n'a pas d'options ou si c'est un tableau vide, créer des options par défaut
  if (!element.options || element.options.length === 0) {
    return {
      ...element,
      options: [
        {
          id: `${element.el_ID}_correct`,
          text: "Correct answer",
          isCorrect: true,
          weight: element.maxScore || 1
        },
        {
          id: `${element.el_ID}_incorrect`,
          text: "Incorrect answer",
          isCorrect: false,
          weight: 0
        }
      ],
      maxScore: element.maxScore || 1
    }
  }
  return element
}

// Utiliser computed pour avoir une référence réactive aux éléments du store, avec normalisation
const elements = computed(() => store.testData.elements.map(el => normalizeElement(el)))
const chartCanvas = ref(null)
const penaltiesChartCanvas = ref(null)
let chartInstance = null
let penaltiesChartInstance = null




// Fonctions utilitaires
const getInteractionTypeLabel = (type) => {
  const labels = {
    choice: 'Choix',
    choiceblock: 'Bloc de choix',
    message: 'Message',
    match: 'Appariement',
    order: 'Ordre',
    textgap: 'Texte à trous',
    pairs: 'Paires'
  }
  return labels[type] || type
}

const hasPenalties = (item) => {
  return item.options?.some(option => option.weight < 0) || false
}

const getMaxPositiveScore = (item) => {
  if (!item.options) return 0
  return item.options
    .filter(option => option.weight > 0)
    .reduce((sum, option) => sum + option.weight, 0)
}

const getMaxNegativeScore = (item) => {
  if (!item.options) return 0
  return item.options
    .filter(option => option.weight < 0)
    .reduce((sum, option) => sum + option.weight, 0)
}

const getCoherenceStatus = (item) => {
  const maxPositive = getMaxPositiveScore(item)
  const maxScore = item.maxScore || 0
  
  if (Math.abs(maxPositive - maxScore) < 0.01) return 'good'
  if (maxPositive > maxScore) return 'warning'
  return 'error'
}

const getCoherenceMessage = (item) => {
  const maxPositive = getMaxPositiveScore(item)
  const maxScore = item.maxScore || 0
  
  if (Math.abs(maxPositive - maxScore) < 0.01) return '✓ Cohérent'
  if (maxPositive > maxScore) return '⚠ Points > maxScore'
  return '✗ Points < maxScore'
}

// Computed properties pour les statistiques
const totalItems = computed(() => elements.value.length)
const totalMaxScore = computed(() => 
  elements.value.reduce((sum, item) => sum + (item.maxScore || 0), 0)
)
const itemsWithPenalties = computed(() => 
  elements.value.filter(item => hasPenalties(item)).length
)

// Vérifier s'il y a des pénalités dans le test
const hasAnyPenalties = computed(() => 
  elements.value.some(item => hasPenalties(item))
)

// Calculer la pénalité maximum pour chaque item
const getMaxPenalty = (item) => {
  if (!item.options) return 0
  const penalties = item.options
    .filter(option => option.weight < 0)
    .map(option => Math.abs(option.weight))
  return penalties.length > 0 ? Math.max(...penalties) : 0
}

// Données pour le graphique des scores
const chartData = computed(() => {
  return {
    labels: elements.value.map((item, index) => `Item ${index + 1}`),
    datasets: [{
      label: 'Score Maximum',
      data: elements.value.map(item => item.maxScore || 0),
      backgroundColor: elements.value.map(item => {
        const score = item.maxScore || 0
        if (score === 1) return '#36A2EB' // Bleu pour score standard
        if (score > 1) return '#FF6384'   // Rouge pour score élevé
        return '#FFCE56'                  // Jaune pour score faible
      }),
      borderColor: '#2C3E50',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }]
  }
})

// Données pour le graphique des pénalités
const penaltiesChartData = computed(() => {
  return {
    labels: elements.value.map((item, index) => `Item ${index + 1}`),
    datasets: [{
      label: 'Pénalité Maximum',
      data: elements.value.map(item => getMaxPenalty(item)),
      backgroundColor: elements.value.map(item => {
        const penalty = getMaxPenalty(item)
        if (penalty === 0) return '#28A745'      // Vert pour aucune pénalité
        if (penalty < 0.5) return '#FFC107'     // Orange pour pénalité légère
        return '#DC3545'                        // Rouge pour pénalité lourde
      }),
      borderColor: '#2C3E50',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }]
  }
})



const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Répartition des scores maximums',
      font: {
        size: 16,
        weight: 'bold'
      }
    },
    legend: {
      display: false // On utilise notre propre légende
    },
    tooltip: {
      callbacks: {
        title: function(context) {
          const index = context[0].dataIndex
          const item = elements.value[index]
          return `${context[0].label} - ${getInteractionTypeLabel(item.el_Type)}`
        },
        label: function(context) {
          return `Score maximum: ${context.parsed.y} point${context.parsed.y > 1 ? 's' : ''}`
        },
        afterLabel: function(context) {
          const index = context.dataIndex
          const item = elements.value[index]
          return item.el_Text.length > 50 ? 
            item.el_Text.substring(0, 50) + '...' : 
            item.el_Text
        }
      }
    }
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Items du test'
      },
      grid: {
        display: false
      }
    },
    y: {
      title: {
        display: true,
        text: 'Score maximum'
      },
      beginAtZero: true,
      ticks: {
        stepSize: 0.5,
        callback: function(value) {
          return value + (value > 1 ? ' pts' : ' pt')
        }
      }
    }
  },
  onClick: (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index
      // Scroll vers l'item correspondant
      const itemCard = document.querySelectorAll('.item-card')[index]
      if (itemCard) {
        itemCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        itemCard.style.animation = 'highlight 2s ease-in-out'
        setTimeout(() => {
          itemCard.style.animation = ''
        }, 2000)
      }
    }
  }
}

const penaltiesChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Répartition des pénalités maximums',
      font: {
        size: 16,
        weight: 'bold'
      }
    },
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        title: function(context) {
          const index = context[0].dataIndex
          const item = elements.value[index]
          return `${context[0].label} - ${getInteractionTypeLabel(item.el_Type)}`
        },
        label: function(context) {
          const penalty = context.parsed.y
          if (penalty === 0) {
            return 'Aucune pénalité'
          }
          return `Pénalité maximum: -${penalty} point${penalty > 1 ? 's' : ''}`
        },
        afterLabel: function(context) {
          const index = context.dataIndex
          const item = elements.value[index]
          const penalties = item.options?.filter(opt => opt.weight < 0) || []
          if (penalties.length > 0) {
            return `${penalties.length} option${penalties.length > 1 ? 's' : ''} avec pénalité`
          }
          return item.el_Text.length > 50 ? 
            item.el_Text.substring(0, 50) + '...' : 
            item.el_Text
        }
      }
    }
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Items du test'
      },
      grid: {
        display: false
      }
    },
    y: {
      title: {
        display: true,
        text: 'Pénalité maximum'
      },
      beginAtZero: true,
      ticks: {
        stepSize: 0.25,
        callback: function(value) {
          if (value === 0) return '0'
          return '-' + value + (value > 1 ? ' pts' : ' pt')
        }
      }
    }
  },
  onClick: (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index
      const itemCard = document.querySelectorAll('.item-card')[index]
      if (itemCard) {
        itemCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        itemCard.style.animation = 'highlight 2s ease-in-out'
        setTimeout(() => {
          itemCard.style.animation = ''
        }, 2000)
      }
    }
  }
}

// Créer le graphique des scores
const createChart = async () => {
  await nextTick()
  if (chartCanvas.value && elements.value.length > 0) {
    if (chartInstance) {
      chartInstance.destroy()
    }
    
    const ctx = chartCanvas.value.getContext('2d')
    chartInstance = new Chart(ctx, {
      type: 'bar',
      data: chartData.value,
      options: chartOptions
    })
  }
}

// Créer le graphique des pénalités
const createPenaltiesChart = async () => {
  await nextTick()
  if (penaltiesChartCanvas.value && elements.value.length > 0 && hasAnyPenalties.value) {
    if (penaltiesChartInstance) {
      penaltiesChartInstance.destroy()
    }
    
    const ctx = penaltiesChartCanvas.value.getContext('2d')
    penaltiesChartInstance = new Chart(ctx, {
      type: 'bar',
      data: penaltiesChartData.value,
      options: penaltiesChartOptions
    })
  }
}

// Mettre à jour les graphiques
const updateChart = () => {
  if (chartInstance) {
    chartInstance.data = chartData.value
    chartInstance.update('active')
  }
}

const updatePenaltiesChart = () => {
  if (penaltiesChartInstance && hasAnyPenalties.value) {
    penaltiesChartInstance.data = penaltiesChartData.value
    penaltiesChartInstance.update('active')
  } else if (!hasAnyPenalties.value && penaltiesChartInstance) {
    // Détruire le graphique s'il n'y a plus de pénalités
    penaltiesChartInstance.destroy()
    penaltiesChartInstance = null
  } else if (hasAnyPenalties.value && !penaltiesChartInstance) {
    // Créer le graphique s'il y a maintenant des pénalités
    createPenaltiesChart()
  }
}

const updateAllCharts = () => {
  updateChart()
  updatePenaltiesChart()
}

// Initialiser les options manquantes pour les items existants
const initializeMissingOptions = async () => {
  let hasUpdates = false

  store.testData.elements.forEach(element => {
    // Si l'élément n'a pas d'options ou si c'est un tableau vide
    if (!element.options || element.options.length === 0) {
      console.log(`🔧 Initializing default options for ${element.el_Type} item: ${element.el_ID}`)

      // Créer les options par défaut
      element.options = [
        {
          id: `${element.el_ID}_correct`,
          text: getDefaultCorrectText(element.el_Type),
          isCorrect: true,
          weight: element.maxScore || 1
        },
        {
          id: `${element.el_ID}_incorrect`,
          text: getDefaultIncorrectText(element.el_Type),
          isCorrect: false,
          weight: 0
        }
      ]

      // S'assurer que maxScore existe
      if (!element.maxScore) {
        element.maxScore = 1
      }

      // Sauvegarder l'élément dans le store
      store.updateElement(element)
      hasUpdates = true
    }
  })

  if (hasUpdates) {
    console.log('✅ Default options initialized and saved for items without options')
  }
}

// Obtenir le texte par défaut pour l'option correcte selon le type d'interaction
const getDefaultCorrectText = (type) => {
  const texts = {
    hotSpot: 'Correct zone(s) clicked',
    order: 'Correct order',
    makingPairs: 'Correct pair(s)',
    match: 'Correct match(es)'
  }
  return texts[type] || 'Correct answer'
}

// Obtenir le texte par défaut pour l'option incorrecte selon le type d'interaction
const getDefaultIncorrectText = (type) => {
  const texts = {
    hotSpot: 'Incorrect zone(s) clicked',
    order: 'Incorrect order',
    makingPairs: 'Incorrect pair(s)',
    match: 'Incorrect match(es)'
  }
  return texts[type] || 'Incorrect answer'
}

// Lifecycle et watchers
onMounted(async () => {
  // Initialiser les options manquantes en premier
  await initializeMissingOptions()

  // Puis créer les graphiques
  createChart()
  createPenaltiesChart()
})

watch(() => elements.value.length, () => {
  createChart()
  createPenaltiesChart()
})

watch(chartData, () => {
  updateChart()
}, { deep: true })

watch(penaltiesChartData, () => {
  updatePenaltiesChart()
}, { deep: true })

watch(hasAnyPenalties, () => {
  if (hasAnyPenalties.value) {
    createPenaltiesChart()
  }
})



// Fonctions de modification
const updateMaxScore = (item) => {
  console.log('💾 Max score updated for item:', item.el_ID, '- New value:', item.maxScore)

  // Trouver l'élément dans le store et le mettre à jour
  const elementInStore = store.testData.elements.find(el => el.el_ID === item.el_ID)
  if (elementInStore) {
    elementInStore.maxScore = item.maxScore
    // Sauvegarder dans le store (qui déclenchera la sauvegarde en DB)
    store.updateElement(elementInStore)
    console.log('✅ Max score saved to store and database')
  } else {
    console.error('❌ Element not found in store:', item.el_ID)
  }

  // Mettre à jour les graphiques
  updateAllCharts()
}

const updateOptionWeight = (item, option) => {
  console.log('💾 Weight updated for option:', option.id, 'in item:', item.el_ID, '- New weight:', option.weight)

  // Trouver l'élément dans le store
  const elementInStore = store.testData.elements.find(el => el.el_ID === item.el_ID)
  if (!elementInStore) {
    console.error('❌ Element not found in store:', item.el_ID)
    return
  }

  // Cas spécial pour HotSpot : synchroniser avec el_Data (zones)
  if (elementInStore.el_Type === 'hotSpot') {
    // Si c'est la pénalité pour mauvais clic
    if (option.id.includes('_wrong_click')) {
      if (!elementInStore.el_data) elementInStore.el_data = {}
      elementInStore.el_data.penalties = -Math.abs(option.weight)  // Stocker en positif
      elementInStore.el_Penalties = -Math.abs(option.weight)
      console.log(`✅ HotSpot penalty updated: ${elementInStore.el_data.penalties}`)
    } else {
      // Sinon, c'est une zone : mettre à jour le score dans el_Data
      const zone = elementInStore.el_Data?.find(z => z.id === option.id)
      if (zone) {
        zone.score = option.weight
        console.log(`✅ HotSpot zone "${zone.name}" score updated: ${zone.score}`)
      }
    }

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour Making Pairs : synchroniser avec el_InteractionData
  else if (elementInStore.el_Type === 'makingPairs') {
    // Mettre à jour pointsPerPair (tous les poids sont identiques)
    if (!elementInStore.el_InteractionData) {
      elementInStore.el_InteractionData = { expectedPairs: [], pointsPerPair: 1 }
    }
    elementInStore.el_InteractionData.pointsPerPair = option.weight

    // Recalculer maxScore
    const nbPairs = elementInStore.el_InteractionData.expectedPairs?.length || 0
    elementInStore.maxScore = nbPairs * option.weight

    console.log(`✅ Making Pairs points per pair updated: ${option.weight}`)

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour Order : synchroniser avec weight
  else if (elementInStore.el_Type === 'order') {
    // Si c'est l'ordre correct, mettre à jour le weight
    if (option.id.includes('_correct_order')) {
      elementInStore.weight = option.weight
      elementInStore.maxScore = option.weight
      console.log(`✅ Order weight updated: ${option.weight}`)
    }
    // Si c'est l'ordre incorrect, on ne fait rien (poids toujours 0)

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour TextPuzzle : synchroniser avec el_InteractionData
  else if (elementInStore.el_Type === 'textPuzzle') {
    if (!elementInStore.el_InteractionData) {
      elementInStore.el_InteractionData = { gaps: [], distractors: [], pointsPerCorrect: 1, penaltyPerIncorrect: 0 }
    }

    // Si c'est un gap (option correcte)
    if (option.id.includes('_gap_')) {
      elementInStore.el_InteractionData.pointsPerCorrect = option.weight
      // Recalculer maxScore
      const nbGaps = elementInStore.el_InteractionData.gaps?.length || 0
      elementInStore.maxScore = nbGaps * option.weight
      console.log(`✅ TextPuzzle points per correct updated: ${option.weight}`)
    }
    // Si c'est un distractor (option incorrecte)
    else if (option.id.includes('_distractor_')) {
      elementInStore.el_InteractionData.penaltyPerIncorrect = Math.abs(option.weight)
      console.log(`✅ TextPuzzle penalty per incorrect updated: ${elementInStore.el_InteractionData.penaltyPerIncorrect}`)
    }

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour TextGaps : synchroniser avec gaps[]
  else if (elementInStore.el_Type === 'textGaps') {
    console.log('🔍 TextGaps: Processing option.id:', option.id)

    // Extraire l'INDEX du gap depuis l'option ID (format: "UUID_gap_0", "UUID_gap_1", etc.)
    const gapIndexMatch = option.id.match(/_gap_(\d+)$/)
    if (gapIndexMatch && elementInStore.gaps) {
      const gapIndex = parseInt(gapIndexMatch[1], 10)
      console.log('🔍 TextGaps: Looking for gap at index:', gapIndex)

      const gap = elementInStore.gaps[gapIndex]
      if (gap) {
        gap.weight = option.weight
        console.log(`✅ TextGaps gap "${gap.label}" at index ${gapIndex} weight updated: ${gap.weight}`)

        // Recalculer maxScore comme somme de tous les poids
        elementInStore.maxScore = elementInStore.gaps.reduce((sum, g) => sum + (g.weight || 1), 0)
        console.log(`✅ TextGaps maxScore recalculated: ${elementInStore.maxScore}`)
      } else {
        console.error(`❌ TextGaps: Gap at index ${gapIndex} not found (array length: ${elementInStore.gaps.length})`)
      }
    } else {
      console.error('❌ TextGaps: Could not match gap index from option.id:', option.id)
    }

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour Match : synchroniser avec el_Data.scoring
  else if (elementInStore.el_Type === 'match') {
    if (!elementInStore.el_Data) {
      elementInStore.el_Data = { rows: [], columns: [], solutionMatrix: [], scoring: { right: 1, wrong: 0 } }
    }
    if (!elementInStore.el_Data.scoring) {
      elementInStore.el_Data.scoring = { right: 1, wrong: 0 }
    }

    // Si c'est une correspondance correcte
    if (option.id.includes('_match_')) {
      elementInStore.el_Data.scoring.right = option.weight
      // Recalculer maxScore
      const solutionMatrix = elementInStore.el_Data.solutionMatrix || []
      let correctMatchesCount = 0
      solutionMatrix.forEach(row => {
        row.forEach(isCorrect => {
          if (isCorrect) correctMatchesCount++
        })
      })
      elementInStore.maxScore = correctMatchesCount * option.weight
      console.log(`✅ Match right match weight updated: ${option.weight}`)
    }
    // Si c'est la pénalité pour mauvaise correspondance
    else if (option.id.includes('_wrong_match')) {
      elementInStore.el_Data.scoring.wrong = Math.abs(option.weight)
      console.log(`✅ Match wrong match penalty updated: ${elementInStore.el_Data.scoring.wrong}`)
    }

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  // Cas spécial pour ShortAnswer : synchroniser avec correctionAxes[]
  else if (elementInStore.el_Type === 'shortAnswer') {
    // Extraire l'index de l'axe et le type (positive/negative) depuis l'option ID
    // Format: "QSA0001_axis_0_positive" ou "QSA0001_axis_0_negative"
    const axisMatch = option.id.match(/_axis_(\d+)_(positive|negative)$/)
    if (axisMatch && elementInStore.correctionAxes) {
      const axisIndex = parseInt(axisMatch[1], 10)
      const axisType = axisMatch[2]
      const axis = elementInStore.correctionAxes[axisIndex]

      if (axis) {
        if (axisType === 'positive') {
          axis.point = option.weight
          console.log(`✅ ShortAnswer axis "${axis.axis}" point updated: ${axis.point}`)
        } else if (axisType === 'negative') {
          axis.penalty = Math.abs(option.weight)
          console.log(`✅ ShortAnswer axis "${axis.axis}" penalty updated: ${axis.penalty}`)
        }

        // Recalculer maxScore comme somme de tous les points positifs
        elementInStore.maxScore = elementInStore.correctionAxes.reduce((sum, ax) => sum + (ax.point || 0), 0)
      }
    }

    // Sauvegarder dans le store
    store.updateElement(elementInStore)
  }
  else {
    // Pour les autres types d'interactions, utiliser options
    const optionInStore = elementInStore.options?.find(opt => opt.id === option.id)
    if (optionInStore) {
      optionInStore.weight = option.weight
      // Sauvegarder dans le store (qui déclenchera la sauvegarde en DB)
      store.updateElement(elementInStore)
      console.log('✅ Option weight saved to store and database')
    } else {
      console.error('❌ Option not found in element:', option.id)
    }
  }

  // Mettre à jour les graphiques car cela peut affecter les pénalités
  updateAllCharts()
}

const normalizeScores = (item) => {
  if (!item.options || !item.maxScore) return

  const correctOptions = item.options.filter(opt => opt.isCorrect)
  if (correctOptions.length === 0) return

  const weightPerCorrect = item.maxScore / correctOptions.length

  // Trouver l'élément dans le store
  const elementInStore = store.testData.elements.find(el => el.el_ID === item.el_ID)
  if (!elementInStore) {
    console.error('❌ Element not found in store:', item.el_ID)
    return
  }

  elementInStore.options.forEach(option => {
    if (option.isCorrect) {
      option.weight = weightPerCorrect
    } else if (option.weight > 0) {
      option.weight = 0
    }
  })

  // Sauvegarder dans le store
  store.updateElement(elementInStore)
  console.log('✅ Scores normalized and saved for item:', item.el_ID)

  // Mettre à jour les graphiques
  updateAllCharts()
}

const resetPenalties = (item) => {
  if (!item.options) return

  // Trouver l'élément dans le store
  const elementInStore = store.testData.elements.find(el => el.el_ID === item.el_ID)
  if (!elementInStore) {
    console.error('❌ Element not found in store:', item.el_ID)
    return
  }

  elementInStore.options.forEach(option => {
    if (option.weight < 0) {
      option.weight = 0
    }
  })

  // Sauvegarder dans le store
  store.updateElement(elementInStore)
  console.log('✅ Penalties reset and saved for item:', item.el_ID)

  // Mettre à jour les graphiques
  updateAllCharts()
}

const equalWeights = (item) => {
  if (!item.options || !item.maxScore) return

  const correctOptions = item.options.filter(opt => opt.isCorrect)
  if (correctOptions.length === 0) return

  const equalWeight = item.maxScore / correctOptions.length

  // Trouver l'élément dans le store
  const elementInStore = store.testData.elements.find(el => el.el_ID === item.el_ID)
  if (!elementInStore) {
    console.error('❌ Element not found in store:', item.el_ID)
    return
  }

  const correctOptionsInStore = elementInStore.options.filter(opt => opt.isCorrect)
  correctOptionsInStore.forEach(option => {
    option.weight = equalWeight
  })

  // Sauvegarder dans le store
  store.updateElement(elementInStore)
  console.log('✅ Equal weights applied and saved for item:', item.el_ID)
}

// Actions globales
const normalizeAllScores = () => {
  elements.value.forEach(item => normalizeScores(item))
  updateAllCharts()
}

const removeAllPenalties = () => {
  elements.value.forEach(item => resetPenalties(item))
  updateAllCharts()
}

const validateScoring = () => {
  const issues = []
  
  elements.value.forEach((item, index) => {
    const maxPositive = getMaxPositiveScore(item)
    const maxScore = item.maxScore || 0
    
    if (Math.abs(maxPositive - maxScore) > 0.01) {
      issues.push(`Item ${index + 1}: Incohérence entre points positifs (${maxPositive}) et maxScore (${maxScore})`)
    }
  })
  
  if (issues.length > 0) {
    alert('Issues détectées:\n' + issues.join('\n'))
  } else {
    alert('✓ Tous les scores sont cohérents!')
  }
}
</script>

<style scoped>
.chart-section {
  margin: 30px 0;
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
}

.chart-section h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 1.3em;
  text-align: center;
}

.chart-container {
  position: relative;
  height: 400px;
  margin-bottom: 15px;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  margin-top: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9em;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #2C3E50;
}

.legend-color.standard {
  background-color: #36A2EB;
}

.legend-color.high {
  background-color: #FF6384;
}

.legend-color.low {
  background-color: #FFCE56;
}

.legend-color.no-penalty {
  background-color: #28A745;
}

.legend-color.light-penalty {
  background-color: #FFC107;
}

.legend-color.heavy-penalty {
  background-color: #DC3545;
}

.no-penalties-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  background: #f8f9fa;
  border: 2px dashed #28a745;
  border-radius: 12px;
  color: #28a745;
}

.no-penalties-icon {
  font-size: 3em;
  margin-bottom: 10px;
}

.no-penalties-message p {
  font-size: 1.2em;
  font-weight: bold;
  margin: 0 0 5px 0;
}

.no-penalties-message small {
  color: #6c757d;
  font-style: italic;
}

@keyframes highlight {
  0% { transform: scale(1); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
  50% { transform: scale(1.02); box-shadow: 0 8px 25px rgba(0,123,255,0.3); }
  100% { transform: scale(1); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
}

.score-board-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  margin-bottom: 30px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.header h2 {
  margin: 0 0 15px 0;
  color: #2c3e50;
}

.test-summary {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.summary-item .label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.summary-item .value {
  font-size: 1.2em;
  font-weight: bold;
  color: #2c3e50;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.item-card {
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 20px;
  background: white;
  transition: all 0.3s ease;
}

.item-card.has-penalties {
  border-color: #ffc107;
  background: #fff9e6;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.item-number {
  background: #007bff;
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9em;
}

.item-type {
  background: #6c757d;
  color: white;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.8em;
}

.badge {
  background: #17a2b8;
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75em;
}

.item-question h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  line-height: 1.4;
}

.rich-text {
  color: #6c757d;
  font-style: italic;
  margin: 0;
}

.scoring-section {
  margin-top: 20px;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.score-info {
  margin-bottom: 20px;
}

.max-score {
  display: flex;
  align-items: center;
  gap: 10px;
}

.max-score label {
  font-weight: 600;
  color: #495057;
}

.score-input {
  padding: 5px 10px;
  border: 2px solid #007bff;
  border-radius: 4px;
  width: 80px;
  font-weight: bold;
}

.options-scoring h5 {
  margin: 0 0 15px 0;
  color: #495057;
}

.options-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-row {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 15px;
  align-items: center;
  padding: 10px;
  border-radius: 6px;
  background: white;
}

.option-row.correct {
  border-left: 4px solid #28a745;
}

.option-row.incorrect {
  border-left: 4px solid #dc3545;
}

.option-row.neutral {
  border-left: 4px solid #6c757d;
}

.option-text {
  font-weight: 500;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
}

.status-badge.correct {
  background: #d4edda;
  color: #155724;
}

.status-badge.incorrect {
  background: #f8d7da;
  color: #721c24;
}

.option-weight {
  display: flex;
  align-items: center;
  gap: 5px;
}

.weight-input {
  width: 60px;
  padding: 3px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
}

.penalty-badge {
  background: #ffc107;
  color: #856404;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: bold;
}

.score-summary {
  margin-top: 15px;
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.positive-score {
  color: #28a745;
  font-weight: bold;
}

.negative-score {
  color: #dc3545;
  font-weight: bold;
}

.coherence-indicator.good {
  color: #28a745;
  font-weight: bold;
}

.coherence-indicator.warning {
  color: #ffc107;
  font-weight: bold;
}

.coherence-indicator.error {
  color: #dc3545;
  font-weight: bold;
}

.quick-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.global-actions {
  margin-top: 30px;
  text-align: center;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.global-actions .btn {
  padding: 12px 24px;
  font-size: 1em;
}

@media (max-width: 768px) {
  .chart-container {
    height: 300px;
  }
  
  .chart-legend {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  
  .option-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .test-summary {
    flex-direction: column;
    gap: 15px;
  }
  
  .quick-actions {
    flex-direction: column;
  }
  
  .global-actions {
    flex-direction: column;
    align-items: center;
  }
}
</style>